import { useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { styled, useMediaQuery, useTheme } from "@mui/material";
import { AppRoutes, landDetailLink } from "../../config";
import { LandToken } from "../../types/LandToken";
import { landLngLat, scaleBar } from "../../utils/staticMap";
import StaticMapBackground, { useStaticMap } from "./StaticMapBackground";
import LandSlide from "./LandSlide";
import MapAttribution from "./MapAttribution";
import { useSwipe } from "./useSwipe";
import { HERO_DOT_EASE, HERO_DOT_MS, HeroMotionMode, riseSx } from "./heroMotion";
import { MOBILE_UNIT_VAR, mobileScale, u } from "./heroScale";
import { INTER, INTER_BLACK, INTER_BOLD } from "./heroFonts";

type HomeHeroProps = {
  /** Featured land first, as EcoBank orders them. One dot per land. */
  lands: LandToken[];
  /**
   * Lands still on their way. The copy column doesn't depend on them, so it renders (and
   * enters) at once; the map panel holds its space empty until the first land arrives, so
   * nothing below the hero jumps.
   */
  loading?: boolean;
};

// Longest the scale bar may run (the design's bar length). The distance it shows follows each
// land's zoom (utils/staticMap staticMapZoom): the longest round step (scaleBar) that fits.
const SCALE_MAX_PX = 152;

/**
 * Homepage hero (Figma 3004:4313): the olive copy column beside a map panel that features one
 * land at a time over a Mapbox static satellite image of its location. Dots, a horizontal swipe
 * on the map (touch) or the arrow keys on the dots switch land.
 * Desktop is a 1 : 2 row 520px tall; below md the copy card (Figma 1809:4215) stacks over a
 * square map panel and the body copy and scale bar drop out. The map panel's overlays are placed
 * in hero units (heroScale), so they keep the frame's proportions at any phone width; all type
 * stays at its design size.
 */
const HomeHero = ({ lands, loading = false }: HomeHeroProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();

  const [index, setIndex] = useState(0);
  // The first land plays the page-load entrance; every later one the short swap, sliding in
  // from the side it came from.
  const [mode, setMode] = useState<HeroMotionMode>("enter");
  const [shownMap, setShownMap] = useState<string | null>(null);

  const panelRef = useRef<HTMLDivElement>(null);
  const land = lands[Math.min(index, lands.length - 1)];
  const lngLat = useMemo(() => landLngLat(land?.meta), [land?.meta]);
  const { url, metresPerCssPx, width } = useStaticMap(panelRef, lngLat, land?.meta);
  const unit = isMobile ? mobileScale(width) : 1;

  const go = (i: number, dir: "next" | "prev") => {
    if (i !== index) {
      setMode(dir);
      setIndex(i);
    }
  };
  // Swipes and arrow keys wrap around the ends; a dot jumps straight to its land.
  const step = (dir: "next" | "prev") => go((index + (dir === "next" ? 1 : -1) + lands.length) % lands.length, dir);
  const swipe = useSwipe(step);

  // Only measure what's actually on screen: no bar over the placeholder or a previous land.
  const scale = url && shownMap === url && metresPerCssPx ? scaleBar(metresPerCssPx, SCALE_MAX_PX) : null;

  return (
    <Root>
      <Copy>
        <Title sx={riseSx(0, "enter")}>A new way to own and trade land{isMobile && "."}</Title>
        <Divider sx={riseSx(1, "enter")} />
        {!isMobile && (
          <Body sx={riseSx(2, "enter")}>
            EcoFutures turns real land into secure digital assets called EcoFuture Tokens (EFTs). Each EFT represents
            verified ownership recorded on‑chain, letting you buy, sell, or assign rights instantly, no middlemen. It’s
            a transparent, future‑ready way to manage land ownership.
          </Body>
        )}
        <Actions sx={riseSx(isMobile ? 2 : 3, "enter")}>
          <HeroButton to={AppRoutes.Help.path} $variant="dark">
            Learn how it works <Arrow aria-hidden>→</Arrow>
          </HeroButton>
          {/* `?id=` flies Explore's map to the featured land and blooms its card (LandCardPopup);
              until the lands arrive it's the bare map. */}
          <HeroButton to={land ? `${AppRoutes.Explore.path}?id=${land.id}` : AppRoutes.Explore.path} $variant="lime">
            Explore EFTs <Arrow aria-hidden>→</Arrow>
          </HeroButton>
        </Actions>
      </Copy>

      <MapPanel
        ref={panelRef}
        {...(lands.length > 1 ? swipe.handlers : {})}
        // A swipe's trailing click isn't a tap on the land.
        onClick={() => land && !swipe.consumeSwipe() && navigate(landDetailLink(land.id))}
        aria-busy={loading || undefined}
        $hasLand={!!land}
      >
        <StaticMapBackground url={url} onShownChange={setShownMap} />
        {land && <LandSlide key={land.id} land={land} isMobile={isMobile} unit={unit} mode={mode} scale={scale} />}
        {lands.length > 1 && (
          <Dots
            $isMobile={isMobile}
            style={{ ["--dot-gap" as any]: isMobile ? u(17) : "12px" }}
            role="group"
            aria-label="Featured lands"
            sx={riseSx(3, "enter")}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") {
                return;
              }
              e.preventDefault();
              const dir = e.key === "ArrowRight" ? "next" : "prev";
              const next = (index + (dir === "next" ? 1 : -1) + lands.length) % lands.length;
              go(next, dir);
              e.currentTarget.querySelectorAll("button")[next]?.focus();
            }}
          >
            <DotRow>
              <ActiveDot
                aria-hidden
                $isMobile={isMobile}
                style={{ transform: `translate3d(${isMobile ? u(index * 27) : `${index * 28}px`}, 0, 0)` }}
              />
              {lands.map((l, i) => (
                <Dot
                  key={l.id}
                  type="button"
                  $isMobile={isMobile}
                  aria-label={`Show ${l.meta?.name || `land ${i + 1}`}`}
                  aria-current={i === index ? "true" : undefined}
                  onClick={() => go(i, i > index ? "next" : "prev")}
                />
              ))}
            </DotRow>
          </Dots>
        )}
        {/* Last, so it's last in the panel's tab order too. */}
        {url && <MapAttribution />}
      </MapPanel>
    </Root>
  );
};

const Root = styled("section")(({ theme }) => ({
  width: "100%",
  display: "grid",
  gridTemplateColumns: "minmax(380px, 1fr) minmax(0, 2fr)",
  minHeight: 520,
  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "minmax(0, 1fr)",
    minHeight: 0,
    // Hero units (heroScale): `--u` is one Figma-frame pixel at this width.
    containerType: "inline-size",
    "--u": MOBILE_UNIT_VAR,
  },
}));

const Copy = styled("div")(({ theme }) => ({
  backgroundColor: "#2b3501",
  borderRadius: 20,
  padding: "25px",
  display: "flex",
  flexDirection: "column",
  color: "#fff",
  minWidth: 0,
  [theme.breakpoints.down("md")]: {
    padding: "15px 24px 27px",
  },
}));

const Title = styled("h2")(({ theme }) => ({
  margin: 0,
  maxWidth: 278,
  fontFamily: INTER,
  fontWeight: INTER_BOLD,
  fontSize: 26,
  lineHeight: "36px",
  color: "#fff",
  [theme.breakpoints.down("md")]: {
    maxWidth: "none",
    fontWeight: INTER_BLACK,
    fontSize: 16,
  },
}));

const Divider = styled("hr")(({ theme }) => ({
  margin: "56px 0 0",
  width: "100%",
  maxWidth: 401,
  height: 0,
  border: "none",
  borderTop: "1px solid #fff",
  [theme.breakpoints.down("md")]: {
    // 263 of the frame's 271px content width: a touch short of the buttons, as designed.
    margin: "14px 0 0",
    width: "97%",
    maxWidth: "none",
  },
}));

const Body = styled("p")({
  margin: "42px 0 32px",
  maxWidth: 412,
  fontFamily: INTER,
  fontWeight: 400,
  fontSize: 16,
  lineHeight: "24px",
  color: "#fff",
});

const Actions = styled("div")(({ theme }) => ({
  marginTop: "auto",
  display: "flex",
  justifyContent: "space-between",
  gap: 13,
  [theme.breakpoints.down("md")]: {
    marginTop: 28,
    flexDirection: "column",
    gap: 16,
  },
}));

// transitions.dev "Learn more hover" (#24), arrow only: it nudges right on hover/focus. Hover
// in is quick (--duration-fast, set on the hover rule below); the return is softer
// (--duration-medium), both on --ease-smooth-out.
const Arrow = styled("span")({
  display: "inline-block",
  marginLeft: 4,
  transition: "transform 350ms cubic-bezier(0.22, 1, 0.36, 1)",
  "@media (prefers-reduced-motion: reduce)": { transition: "none" },
});

// Figma "Component 11": 202 × 44 pill, Inter 14/28.
const HeroButton = styled(Link, {
  shouldForwardProp: (prop) => prop !== "$variant",
})<{ $variant: "dark" | "lime" }>(({ theme, $variant }) => ({
  // 202px each as designed; they share the row and narrow together when the column is tighter
  // than the design's 520px (a 1440px viewport gives it ~480).
  flex: "1 1 0",
  maxWidth: 202,
  minWidth: 0,
  height: 44,
  boxSizing: "border-box",
  padding: "8px 16px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: 20,
  fontFamily: INTER,
  fontWeight: 400,
  fontSize: 14,
  lineHeight: "28px",
  textDecoration: "none",
  whiteSpace: "nowrap",
  backgroundColor: $variant === "dark" ? "#000" : "#ccff99",
  color: $variant === "dark" ? "#fff" : "#000",
  "&:hover": { backgroundColor: $variant === "dark" ? "#333" : "#bdf283" },
  [`&:hover ${Arrow}, &:focus-visible ${Arrow}`]: {
    transform: "translate3d(3px, 0, 0)",
    transitionDuration: "250ms",
  },
  "&:focus-visible": { outline: "2px solid #ccff99", outlineOffset: 2 },
  [theme.breakpoints.down("md")]: {
    flex: "none",
    width: "100%",
    maxWidth: "none",
    padding: "8px 24px",
  },
}));

const MapPanel = styled("div", {
  shouldForwardProp: (prop) => prop !== "$hasLand",
})<{ $hasLand: boolean }>(({ theme, $hasLand }) => ({
  position: "relative",
  minHeight: 518,
  borderRadius: 20,
  overflow: "hidden",
  backgroundColor: "#000",
  cursor: $hasLand ? "pointer" : "default",
  // Vertical drags scroll the page; horizontal ones are the swipe (useSwipe).
  touchAction: "pan-y",
  // Own stacking context, so the overlays' z-indices stay scoped to the panel.
  isolation: "isolate",
  [theme.breakpoints.down("md")]: {
    minHeight: 0,
    aspectRatio: "1 / 1",
    borderRadius: u(20),
  },
}));

const DOT_SHADOW = "0 4px 14px rgba(0, 0, 0, 0.25)";

// Full-width strip, so the row centres without a transform of its own — the rise owns it.
const Dots = styled("div", {
  shouldForwardProp: (prop) => prop !== "$isMobile",
})<{ $isMobile: boolean }>(({ $isMobile }) => ({
  position: "absolute",
  left: 0,
  right: 0,
  bottom: $isMobile ? u(36) : 57,
  display: "flex",
  justifyContent: "center",
  pointerEvents: "none",
  zIndex: 3,
}));

const DotRow = styled("div")({
  position: "relative",
  display: "flex",
  gap: "var(--dot-gap)",
  pointerEvents: "auto",
});

const Dot = styled("button", {
  shouldForwardProp: (prop) => prop !== "$isMobile",
})<{ $isMobile: boolean }>(({ $isMobile }) => ({
  width: $isMobile ? u(10) : 16,
  height: $isMobile ? u(10) : 16,
  padding: 0,
  border: "none",
  borderRadius: "50%",
  backgroundColor: "rgba(204, 255, 153, 0.4)",
  boxShadow: DOT_SHADOW,
  cursor: "pointer",
  // A bigger hit area than the 10–16px dot.
  position: "relative",
  "&::after": { content: '""', position: "absolute", inset: -8 },
  "&:focus-visible": { outline: "2px solid #ccff99", outlineOffset: 3 },
}));

// The lit dot slides between positions (transitions.dev "Tabs sliding" #16), over the dimmed row.
const ActiveDot = styled("span", {
  shouldForwardProp: (prop) => prop !== "$isMobile",
})<{ $isMobile: boolean }>(({ $isMobile }) => ({
  position: "absolute",
  left: 0,
  top: 0,
  width: $isMobile ? u(10) : 16,
  height: $isMobile ? u(10) : 16,
  borderRadius: "50%",
  backgroundColor: "#ccff99",
  pointerEvents: "none",
  zIndex: 1,
  transition: `transform ${HERO_DOT_MS}ms ${HERO_DOT_EASE}`,
  "@media (prefers-reduced-motion: reduce)": { transition: "none" },
}));

export default HomeHero;
